const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

/**
 * THE STAGGER IS CAPPED, AND IT NO LONGER WIPES THE STYLE ATTRIBUTE.
 *
 * Dawn writes `setAttribute('style', '--animation-order: N')`, which REPLACES
 * the whole attribute — including the `--animation-order` the section's Liquid
 * authored from `forloop.index`, and anything else an element happens to carry
 * inline. `style.setProperty` writes the one property and leaves the rest.
 *
 * `index` is the element's position in this IntersectionObserver CALLBACK
 * BATCH, not in the DOM — docs/29 §1.5 flags that as a quirk. Measured on this
 * theme it is the better of the two available orders: a batch is in practice
 * one grid ROW, so the delay resets per row and a card that crosses the
 * threshold alone gets 0 and reveals immediately, which is what you want. The
 * Liquid value is a GLOBAL index, so on a 24-card collection grid the last
 * card would wait 24 x 75ms = 1.8s.
 *
 * What the batch index cannot do on its own is bound itself: a short page, a
 * fast scroll or a filter render can put a whole grid in one batch. Capping it
 * holds the longest delay to CASCADE_MAX x 75ms = 375ms, so the last card in
 * any batch starts within a reveal-duration of the first. base.css caps it a
 * second time, so the Liquid path is bounded too even before this runs.
 */
const CASCADE_MAX = 5;

function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade')) {
          elementTarget.style.setProperty('--animation-order', String(Math.min(index, CASCADE_MAX)));
        }
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

/**
 * docs/29 §1.6 — reveals are STRIPPED BELOW 768.
 *
 * Dawn reveals on mobile; our spec does not. Two reasons, both in docs/29 §1:
 * IntersectionObserver work and forced compositing on the lowest-powered
 * devices, and 390px is the dominant viewport for this catalogue (docs/47
 * §18.7). It also helps anyone motion-sensitive who has not set the OS flag.
 *
 * Implemented by clearing the offscreen class outright rather than by simply
 * not observing — an element that keeps `scroll-trigger--offscreen` and is
 * never observed stays invisible forever. The CSS side of this gate is in
 * base.css section 11; both exist because either alone leaves a hole (CSS
 * alone still pays for the observer, JS alone flashes before hydration).
 *
 * docs/51 §10 risk 6 flags this as a thing Dawn gets wrong for us.
 */
const SCROLL_ANIMATION_MOBILE_BREAKPOINT = 768;

function scrollRevealsDisabled() {
  return window.matchMedia(`(max-width: ${SCROLL_ANIMATION_MOBILE_BREAKPOINT - 1}px)`).matches;
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (scrollRevealsDisabled()) {
    animationTriggerElements.forEach((element) => {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    });
    return;
  }

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true },
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
